import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { AbstractControl } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { Localisation } from '../modeles/localisation.modele';
import { FormattedLocalisation } from '../modeles/formatted-localisation.modele';
import { LocalisationRepresentantApiService } from './localisation-representant-api.service';
import { AppConfigService } from '../../app-config.service';

interface NormalizedLocalisationOption {
  localisation: FormattedLocalisation;
  normalizedDescription: string;
  inputStringIndex: number;
}

@Injectable({
  providedIn: 'root'
})
export class LocalisationRepresentantAutocompleteService {
  private readonly filteredLocalisations: BehaviorSubject<FormattedLocalisation[]> = new BehaviorSubject([]);
  public filteredLocalisationsChanged$: Observable<FormattedLocalisation[]> = this.filteredLocalisations.asObservable();

  private autocompleteFormControl: AbstractControl = null;

  private readonly descriptionKey: string;

  private localisationOptions: NormalizedLocalisationOption[] = [];

  private debounceTime = 200;

  constructor(
    private readonly _localisationApi: LocalisationRepresentantApiService,
    private readonly appConfigService: AppConfigService
  ) {
    const langue = this.appConfigService.configData.contexteEnvironnement.langue;
    this.descriptionKey = langue === 'en' ? 'englishDescription' : 'frenchDescription';
    this.initOptions();
  }

  public setFormControl(formControl: AbstractControl): void {
    this.autocompleteFormControl = formControl;
    this.autocompleteFormControl.valueChanges.pipe(debounceTime(this.debounceTime)).subscribe(inputString => {
      const isInputStringValid = inputString !== null && typeof inputString === 'string' && inputString.length > 0;
      this.filteredLocalisations.next(isInputStringValid ? this.filterAutocompleteOptions(inputString) : []);
    });
  }

  public requireSelectionValidator(control: AbstractControl) {
    if (typeof control.value === 'string') {
      return { noSelection: true };
    }
    return null;
  }

  public formatLocalisation(localisation: Localisation): FormattedLocalisation {
    return { id: localisation.id, description: this.formatLocalisationDescription(localisation) };
  }

  public setDebounceTime(time: number) {
    this.debounceTime = time;
  }

  private filterAutocompleteOptions(inputString: string): FormattedLocalisation[] {
    const normalizedInputString = this.normalizeString(inputString.toLowerCase());

    return this.localisationOptions
      .map(option => {
        option.inputStringIndex = option.normalizedDescription.indexOf(normalizedInputString);
        return option;
      })
      .filter(option => {
        if (option.inputStringIndex < 0 || (inputString.length < 2 && option.inputStringIndex !== 0)) {
          return false;
        }
        return true;
      })
      .sort(this.sortLocalisationResults)
      .map(option => option.localisation);
  }

  private normalizeString(string: string): string {
    return this.removeSpecialCharacters(string.toLowerCase());
  }

  private removeSpecialCharacters(string: string): string {
    return string.normalize('NFD').replace(/[^a-z0-9]/g, '');
  }

  private sortLocalisationResults(resultA, resultB) {
    // If the result starts with the input string, show it first
    if (resultA.inputStringIndex === 0 && resultB.inputStringIndex !== 0) {
      return -1;
    }
    if (resultA.inputStringIndex !== 0 && resultB.inputStringIndex === 0) {
      return 1;
    }

    // Else sort alphabetically
    if (resultA.normalizedDescription < resultB.normalizedDescription) {
      return -1;
    } else if (resultA.normalizedDescription > resultB.normalizedDescription) {
      return 1;
    }
    return 0;
  }

  private initOptions(): void {
    this._localisationApi
      .getLocalisations()
      .toPromise()
      .then(localisations => {
        this.localisationOptions = localisations
          .map(rawLocalisation => this.formatLocalisation(rawLocalisation))
          .map(option => ({
            localisation: option,
            normalizedDescription: this.normalizeString(option.description),
            inputStringIndex: 0
          }));
      });
  }

  private formatLocalisationDescription(localisation: Localisation): string {
    const description: string[] = [];
    if (localisation.arrondissement) {
      description.push(localisation.arrondissement[this.descriptionKey]);
    }
    if (localisation.ville) {
      description.push(localisation.ville[this.descriptionKey]);
    }
    if (localisation.region) {
      description.push(localisation.region[this.descriptionKey]);
    }
    return description.join(', ');
  }
}
